import React, { useState, useEffect } from "react";
import { Modal, Checkbox, InputNumber, Button, Divider, message, Row, Col, Spin } from "antd";
import { getColumnConfig } from "../RegFormTable/columnConfig";
import "./ColumnPickerModal.css";

const ColumnPickerModal = ({ visible, onClose, onSave, sportType, currentPreferences, isBusy }) => {
  const [preferences, setPreferences] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (visible && currentPreferences) {
      setPreferences(currentPreferences.columns || {});
      setHasChanges(false);
    }
  }, [visible, currentPreferences]);

  const columnConfig = getColumnConfig(sportType);

  // Group columns by category
  const groupedColumns = {};
  Object.keys(columnConfig).forEach((key) => {
    const col = columnConfig[key];
    if (!groupedColumns[col.category]) {
      groupedColumns[col.category] = [];
    }
    groupedColumns[col.category].push({ ...col, key });
  });

  const handleVisibilityChange = (columnKey, checked) => {
    setPreferences((prev) => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        visible: checked
      }
    }));
    setHasChanges(true);
  };

  const handleWidthChange = (columnKey, width) => {
    setPreferences((prev) => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        width: width || prev[columnKey]?.width
      }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!hasChanges) {
      message.info("No changes to save");
      onClose();
      return;
    }

    const preferencesData = {
      columns: preferences
    };

    onSave(preferencesData);
  };

  const handleReset = () => {
    Modal.confirm({
      title: "Reset to Defaults",
      content: "Are you sure you want to reset all columns to default settings?",
      onOk: () => {
        const defaults = {};
        Object.keys(columnConfig).forEach((key) => {
          defaults[key] = {
            visible: columnConfig[key].defaultVisible,
            width: columnConfig[key].defaultWidth
          };
        });
        setPreferences(defaults);
        setHasChanges(true);
        message.success("Reset to defaults");
      }
    });
  };

  return (
    <Modal
      title="Customize Table Columns"
      visible={visible}
      onCancel={onClose}
      width={800}
      className="column-picker-modal"
      footer={[
        <Button key="reset" onClick={handleReset} disabled={isBusy}>
          Reset to Defaults
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={isBusy}>
          Save
        </Button>
      ]}
      maskClosable={false}
    >
      <Spin spinning={isBusy}>
        <div className="column-picker-content">
          <div style={{ marginBottom: 16, color: "#666" }}>
            Select which columns to display and adjust their widths. Columns marked as "locked" are always visible.
          </div>

          {Object.keys(groupedColumns).map((category) => (
            <div key={category} className="column-category">
              <Divider orientation="left" style={{ fontWeight: 600, marginTop: 20, marginBottom: 12 }}>
                {category}
              </Divider>
              {groupedColumns[category].map((col) => {
                const pref = preferences[col.key] || {
                  visible: col.defaultVisible,
                  width: col.defaultWidth
                };

                return (
                  <Row key={col.key} gutter={16} style={{ marginBottom: 12 }} align="middle">
                    <Col span={1}>
                      {col.locked ? (
                        <Checkbox checked={true} disabled />
                      ) : (
                        <Checkbox
                          checked={pref.visible}
                          onChange={(e) => handleVisibilityChange(col.key, e.target.checked)}
                        />
                      )}
                    </Col>
                    <Col span={11}>
                      <span style={{ fontWeight: col.locked ? 600 : 400 }}>
                        {col.pickerLabel || col.label}
                        {col.locked && <span style={{ color: "#999", fontSize: 12, marginLeft: 8 }}>(locked)</span>}
                      </span>
                    </Col>
                    <Col span={3} style={{ textAlign: "right", paddingRight: 8 }}>
                      Width:
                    </Col>
                    <Col span={4}>
                      <InputNumber
                        min={50}
                        max={500}
                        value={pref.width}
                        onChange={(value) => handleWidthChange(col.key, value)}
                        size="small"
                        style={{ width: "70px" }}
                      />
                    </Col>
                    <Col span={3} style={{ color: "#999", fontSize: 12 }}>
                      px
                    </Col>
                  </Row>
                );
              })}
            </div>
          ))}
        </div>
      </Spin>
    </Modal>
  );
};

export default ColumnPickerModal;
